/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 📄 File: pages/apps/SwitchboardSSOPage.tsx
 🧩 Description:
    SSO redirect page that generates a token and opens Switchboard
    in a new browser tab with automatic authentication.
────────────────────────────────────────────────────────────────*/

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ExternalLink, AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api';
import { useContextStore } from '../../store/context';

export default function SwitchboardSSOPage() {
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);
    const [redirecting, setRedirecting] = useState(true);
    const { selectedDomain } = useContextStore();

    useEffect(() => {
        generateSSOAndRedirect();
    }, []);

    const generateSSOAndRedirect = async () => {
        try {
            // Generate SSO token from Admin API
            // Include the selected domain from the global context
            const response = await api.post('/api/apps/switchboard/sso-token', {
                domainId: selectedDomain?.id
            });
            const { token, switchboardUrl } = response.data;

            // Build redirect URL with token
            // Use the switchboardUrl from config, or default to current origin with HTTPS
            let baseUrl = switchboardUrl || '/switchboard';

            // If baseUrl is relative, build absolute URL using the current protocol
            if (baseUrl.startsWith('/')) {
                const protocol = window.location.protocol; // Use same protocol as current page
                baseUrl = `${protocol}//${window.location.host}${baseUrl}`;
            }

            const redirectUrl = `${baseUrl}/?sso_token=${encodeURIComponent(token)}`;

            // Open in new tab
            window.open(redirectUrl, '_blank');

            // Go back to previous page or dashboard
            setRedirecting(false);
            setTimeout(() => {
                navigate(-1);
            }, 1500);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            console.error('SSO Error:', err);
            setError(err.response?.data?.error || 'Failed to generate SSO token');
            setRedirecting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-900">
            <div className="text-center p-8 bg-slate-800 rounded-xl border border-slate-700 max-w-md">
                {redirecting ? (
                    <>
                        <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-white mb-2">
                            Opening Switchboard
                        </h2>
                        <p className="text-slate-400">
                            A new tab should open shortly with the Switchboard dashboard...
                        </p>
                    </>
                ) : error ? (
                    <>
                        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-white mb-2">
                            Unable to Open Switchboard
                        </h2>
                        <p className="text-red-400 mb-4">{error}</p>
                        <button
                            onClick={() => navigate(-1)}
                            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
                        >
                            Go Back
                        </button>
                    </>
                ) : (
                    <>
                        <ExternalLink className="w-12 h-12 text-green-500 mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-white mb-2">
                            Switchboard Opened
                        </h2>
                        <p className="text-slate-400">
                            Returning to previous page...
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
